import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ClipboardPaste,
  Copy,
  GripVertical,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { researchCategoryClasses } from "#shared/researchCategoryColors.ts";
import type { IdeaCard, IdeaDetail } from "#shared/schemas/idea.ts";
import type { ResearchNote } from "#shared/schemas/research.ts";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { QueryErrorState } from "@web/features/diagnostics/QueryErrorState.tsx";
import {
  useCopyResearchPrompt,
  useCreateIdeaCard,
  useDeleteIdeaCard,
  useDismissResearchNote,
  useDuplicateIdeaCard,
  useIdeaDetail,
  useIngestResearch,
  useReorderIdeaCard,
  useResearchCategories,
  useUpdateIdea,
  useUpdateIdeaCard,
} from "@web/features/ideas/ideasApi.ts";
import { CategoryCombobox } from "@web/features/ideas/CategoryCombobox.tsx";
import { cn } from "@web/lib/utils.ts";

type StackView = "arc" | "category";

function confidenceLetter(c: string | null): string {
  if (c === "high") return "H";
  if (c === "medium") return "M";
  if (c === "low") return "L";
  return "";
}

function isHttpUrl(s: string): boolean {
  return /^https?:\/\//i.test(s.trim());
}

function categoryKey(card: IdeaCard): string {
  if (card.category?.trim()) return card.category.trim().toLowerCase();
  if (card.kind === "user") return "yours";
  if (card.kind === "machine") return "verified";
  return "other";
}

function categoryLabel(key: string): string {
  if (key === "yours") return "Yours";
  if (key === "verified") return "Verified";
  if (key === "other") return "Other";
  return key;
}

function groupByCategory(cards: IdeaCard[]): { key: string; cards: IdeaCard[] }[] {
  const order: string[] = [];
  const map = new Map<string, IdeaCard[]>();
  for (const card of cards) {
    const key = categoryKey(card);
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(card);
  }
  return order.map((key) => ({ key, cards: map.get(key)! }));
}

function CategoryChip({
  category,
  confidence,
  edited,
  kind,
  onSources,
  onEditCategory,
}: {
  category: string | null;
  confidence: string | null;
  edited: boolean;
  kind: IdeaCard["kind"];
  onSources?: () => void;
  onEditCategory?: () => void;
}) {
  if (kind === "machine") {
    return (
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded-sm border border-accent/40 bg-accent/10 px-1.5 py-0.5 text-xs font-semibold tracking-wider text-accent uppercase">
          Verified
        </span>
        <button
          type="button"
          title="MusicBrainz source"
          onClick={(e) => {
            e.stopPropagation();
            onSources?.();
          }}
          className={cn(
            "inline-flex size-2.5 rounded-full border border-accent",
            edited ? "bg-transparent" : "bg-accent",
          )}
        />
      </div>
    );
  }

  if (kind === "user" && !category && !onEditCategory) return null;

  if (kind === "user") {
    const colors = researchCategoryClasses(category);
    return (
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          title="Change category"
          onClick={(e) => {
            e.stopPropagation();
            onEditCategory?.();
          }}
          className={cn(
            "rounded-sm border px-1.5 py-0.5 text-xs font-semibold tracking-wider uppercase",
            category
              ? cn(colors.text, colors.bg, colors.border)
              : "border-line bg-panel text-faint hover:text-muted",
          )}
        >
          {category ?? "Add category"}
        </button>
      </div>
    );
  }

  // research
  const colors = researchCategoryClasses(category);
  const letter = confidenceLetter(confidence);
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <button
        type="button"
        title="Change category"
        onClick={(e) => {
          e.stopPropagation();
          onEditCategory?.();
        }}
        className={cn(
          "rounded-sm border px-1.5 py-0.5 text-xs font-semibold tracking-wider uppercase",
          colors.text,
          colors.bg,
          colors.border,
        )}
      >
        {category ?? "research"}
      </button>
      <button
        type="button"
        title="Sources"
        onClick={(e) => {
          e.stopPropagation();
          onSources?.();
        }}
        className={cn(
          "inline-flex items-center gap-1 text-xs font-semibold",
          colors.text,
          "hover:text-text",
        )}
      >
        <span
          className={cn(
            "inline-flex size-2.5 rounded-full border",
            colors.border,
            edited ? "bg-transparent" : colors.bar,
            confidence === "low" && "opacity-60",
          )}
        />
        {letter}
      </button>
    </div>
  );
}

function ComposeCard({
  card,
  ideaId,
  canReorder,
  dragHandle,
}: {
  card: IdeaCard;
  ideaId: string;
  canReorder: boolean;
  dragHandle?: {
    setActivatorNodeRef: (el: HTMLElement | null) => void;
    listeners: React.HTMLAttributes<HTMLElement> | undefined;
    attributes: React.HTMLAttributes<HTMLElement>;
  };
}) {
  const update = useUpdateIdeaCard(ideaId);
  const remove = useDeleteIdeaCard(ideaId);
  const duplicate = useDuplicateIdeaCard(ideaId);
  const categories = useResearchCategories();
  const [editing, setEditing] = useState(false);
  const [editingCategory, setEditingCategory] = useState(false);
  const [draft, setDraft] = useState(card.body);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const colors = researchCategoryClasses(card.category);
  const showCategoryBar = Boolean(card.category) && card.kind !== "machine";

  useEffect(() => {
    setDraft(card.body);
  }, [card.body]);

  useEffect(() => {
    return () => {
      if (clickTimer.current) clearTimeout(clickTimer.current);
    };
  }, []);

  function copyBody() {
    void navigator.clipboard.writeText(card.body).then(() => {
      toast.success("Copied");
    });
  }

  function commitEdit() {
    const next = draft.trim();
    if (!next || next === card.body) {
      setDraft(card.body);
      setEditing(false);
      return;
    }
    update.mutate({ cardId: card.id, body: next }, { onSuccess: () => setEditing(false) });
  }

  return (
    <article
      className={cn(
        "group relative overflow-hidden rounded-lg border border-line bg-panel-elevated p-4 transition-colors",
        "hover:border-muted hover:bg-panel",
        card.confidence === "low" && card.kind === "research" && "bg-research-low/50",
      )}
    >
      {showCategoryBar && (
        <span className={cn("absolute top-0 bottom-0 left-0 w-1", colors.bar)} aria-hidden />
      )}

      <div className="flex gap-3 pl-1">
        {canReorder && dragHandle && (
          <button
            type="button"
            ref={dragHandle.setActivatorNodeRef}
            className="mt-1 hidden size-7 shrink-0 cursor-grab items-center justify-center rounded-md text-faint hover:bg-panel-elevated hover:text-muted active:cursor-grabbing group-hover:inline-flex"
            title="Drag to reorder"
            {...dragHandle.listeners}
            {...dragHandle.attributes}
          >
            <GripVertical className="size-4" />
          </button>
        )}

        <div className="min-w-0 flex-1">
          {editingCategory ? (
            <div className="mb-2 max-w-56" onClick={(e) => e.stopPropagation()}>
              <CategoryCombobox
                value={card.category}
                categories={categories.data ?? []}
                aria-label="Card category"
                autoOpen
                onChange={(next) => {
                  update.mutate(
                    { cardId: card.id, category: next },
                    { onSuccess: () => setEditingCategory(false) },
                  );
                }}
                onDismiss={() => setEditingCategory(false)}
              />
            </div>
          ) : (
            <CategoryChip
              category={card.category}
              confidence={card.confidence}
              edited={card.edited}
              kind={card.kind}
              onEditCategory={
                card.kind === "machine" ? undefined : () => setEditingCategory(true)
              }
              onSources={() => {
                if (card.kind === "machine" && card.source_url) {
                  window.open(card.source_url, "_blank", "noreferrer");
                  return;
                }
                setSourcesOpen((v) => !v);
              }}
            />
          )}

          {editing ? (
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setDraft(card.body);
                  setEditing(false);
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  commitEdit();
                }
              }}
              className="min-h-20 w-full resize-y rounded-md border border-line bg-panel px-3 py-2 text-compose text-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
            />
          ) : (
            <button
              type="button"
              className="w-full text-left text-compose text-text"
              title="Click to copy · double-click to edit"
              onClick={() => {
                if (clickTimer.current) clearTimeout(clickTimer.current);
                clickTimer.current = setTimeout(() => {
                  clickTimer.current = null;
                  copyBody();
                }, 220);
              }}
              onDoubleClick={(e) => {
                e.preventDefault();
                if (clickTimer.current) {
                  clearTimeout(clickTimer.current);
                  clickTimer.current = null;
                }
                setEditing(true);
              }}
            >
              {card.body}
            </button>
          )}

          {sourcesOpen && card.kind === "research" && (
            <div className="mt-3 space-y-1.5 rounded-md border border-line bg-panel px-3 py-2.5 text-sm text-muted">
              <p className="text-xs font-semibold tracking-wider text-faint uppercase">Sources</p>
              {(card.sources ?? []).map((src) =>
                isHttpUrl(src) ? (
                  <a
                    key={src}
                    href={src}
                    target="_blank"
                    rel="noreferrer"
                    className="block break-all text-accent hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {src}
                  </a>
                ) : (
                  <p key={src}>{src}</p>
                ),
              )}
              {card.conflict && (
                <p className="pt-1 text-status-working">Conflict: {card.conflict}</p>
              )}
              {card.detail && <p className="pt-1 leading-relaxed text-muted">{card.detail}</p>}
            </div>
          )}
        </div>

        <div className="hidden shrink-0 flex-col gap-1 group-hover:flex">
          <button
            type="button"
            title="Duplicate"
            className="inline-flex size-7 items-center justify-center rounded-md text-faint hover:bg-panel hover:text-text"
            onClick={() => duplicate.mutate(card.id)}
          >
            <Copy className="size-3.5" />
          </button>
          <button
            type="button"
            title="Delete card"
            className="inline-flex size-7 items-center justify-center rounded-md text-faint hover:bg-panel hover:text-destructive"
            onClick={() => remove.mutate(card.id)}
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
}

function DraggableComposeCard({
  card,
  ideaId,
  canReorder,
}: {
  card: IdeaCard;
  ideaId: string;
  canReorder: boolean;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, isDragging } =
    useDraggable({
      id: card.id,
      disabled: !canReorder,
    });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop:${card.id}`,
    disabled: !canReorder,
  });

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        setDropRef(node);
      }}
      style={{ transform: CSS.Translate.toString(transform) }}
      className={cn(isDragging && "opacity-40", isOver && canReorder && "ring-1 ring-accent/50 rounded-lg")}
    >
      <ComposeCard
        card={card}
        ideaId={ideaId}
        canReorder={canReorder}
        dragHandle={
          canReorder
            ? { setActivatorNodeRef, listeners, attributes }
            : undefined
        }
      />
    </div>
  );
}

function InsertGap({
  onInsert,
  active,
}: {
  onInsert: () => void;
  active: boolean;
}) {
  if (!active) return <div className="h-2" />;
  return (
    <div className="group/gap relative h-3">
      <button
        type="button"
        title="Add card here"
        onClick={onInsert}
        className={cn(
          "absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center gap-2",
          "opacity-0 transition-opacity group-hover/gap:opacity-100 focus-visible:opacity-100",
        )}
      >
        <span className="h-px flex-1 bg-line" />
        <span className="inline-flex size-6 items-center justify-center rounded-full border border-line bg-panel text-muted hover:border-accent hover:text-accent">
          <Plus className="size-3.5" />
        </span>
        <span className="h-px flex-1 bg-line" />
      </button>
    </div>
  );
}

function ResearchNoteBanner({
  note,
  ideaId,
}: {
  note: ResearchNote;
  ideaId: string;
}) {
  const dismiss = useDismissResearchNote(ideaId);
  return (
    <div className="mb-5 rounded-lg border border-line bg-panel px-4 py-3.5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-text">
            Research · {note.coverage.replaceAll("_", " ")}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-muted">{note.coverage_note}</p>
          {note.gaps.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold tracking-wider text-research uppercase">
                Worth digging
              </p>
              <ul className="mt-1.5 space-y-1">
                {note.gaps.map((gap) => (
                  <li key={gap} className="text-sm leading-relaxed text-text">
                    <span className="mr-2 text-research">·</span>
                    {gap}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <button
          type="button"
          aria-label="Dismiss research note"
          className="shrink-0 rounded-md p-1 text-faint hover:bg-panel-elevated hover:text-text"
          onClick={() => dismiss.mutate(note.id)}
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}

function PasteResearchPanel({
  ideaId,
  onClose,
}: {
  ideaId: string;
  onClose: () => void;
}) {
  const ingest = useIngestResearch(ideaId);
  const [pasted, setPasted] = useState("");

  return (
    <div className="mb-6 rounded-lg border border-line bg-panel-elevated p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Paste research</h2>
        <button type="button" className="text-faint hover:text-text" onClick={onClose}>
          <X className="size-4" />
        </button>
      </div>
      <p className="mb-3 text-sm text-muted">
        Paste the AI reply (readable summary + JSON). Only the JSON block is ingested.
      </p>
      <textarea
        value={pasted}
        onChange={(e) => setPasted(e.target.value)}
        placeholder='Paste here — include the ```json block from "— JSON for CoyKoi below —"'
        className="min-h-48 w-full resize-y rounded-md border border-line bg-panel px-3 py-2 font-mono text-sm text-text placeholder:text-faint focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
      />
      <div className="mt-3 flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!pasted.trim() || ingest.isPending}
          onClick={() =>
            ingest.mutate(pasted, {
              onSuccess: () => {
                setPasted("");
                onClose();
              },
            })
          }
        >
          Ingest
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function AddCardForm({
  ideaId,
  afterId,
  beforeId,
  onDone,
}: {
  ideaId: string;
  afterId?: string | null;
  beforeId?: string | null;
  onDone: () => void;
}) {
  const create = useCreateIdeaCard(ideaId);
  const categories = useResearchCategories();
  const [draft, setDraft] = useState("");
  const [category, setCategory] = useState<string | null>(null);

  return (
    <div className="mb-2 rounded-lg border border-dashed border-line bg-panel px-4 py-3">
      <div className="mb-2 max-w-56">
        <CategoryCombobox
          value={category}
          categories={categories.data ?? []}
          aria-label="Card category"
          onChange={setCategory}
        />
      </div>
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="One speakable sentence…"
        className="min-h-16 w-full resize-y rounded-md border border-line bg-panel-elevated px-3 py-2 text-compose focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
      />
      <div className="mt-2 flex gap-2">
        <Button
          size="sm"
          disabled={!draft.trim() || create.isPending}
          onClick={() =>
            create.mutate(
              {
                body: draft.trim(),
                category,
                ...(afterId !== undefined ? { after_id: afterId } : {}),
                ...(beforeId !== undefined ? { before_id: beforeId } : {}),
              },
              {
                onSuccess: () => {
                  setDraft("");
                  setCategory(null);
                  onDone();
                },
              },
            )
          }
        >
          Add
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function ComposeStack({ idea }: { idea: IdeaDetail }) {
  const reorder = useReorderIdeaCard(idea.id);
  const [view, setView] = useState<StackView>("arc");
  const [pasteOpen, setPasteOpen] = useState(false);
  const [insertAt, setInsertAt] = useState<
    | { kind: "end" }
    | { kind: "after"; afterId: string }
    | { kind: "before"; beforeId: string }
    | null
  >(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const groups = useMemo(() => groupByCategory(idea.cards), [idea.cards]);
  const activeCard = idea.cards.find((c) => c.id === activeId) ?? null;
  const canReorder = view === "arc";

  function toggleGroup(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function onDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const overId = String(over.id).replace(/^drop:/, "");
    if (overId === active.id) return;
    const from = idea.cards.findIndex((c) => c.id === active.id);
    const to = idea.cards.findIndex((c) => c.id === overId);
    if (from < 0 || to < 0 || from === to) return;
    if (from < to) {
      reorder.mutate({ cardId: String(active.id), after_id: overId });
    } else {
      reorder.mutate({ cardId: String(active.id), before_id: overId });
    }
  }

  return (
    <div>
      {idea.research_notes.map((note) => (
        <ResearchNoteBanner key={note.id} note={note} ideaId={idea.id} />
      ))}

      {pasteOpen && (
        <PasteResearchPanel ideaId={idea.id} onClose={() => setPasteOpen(false)} />
      )}

      {idea.cards.length > 0 && (
        <div className="mb-4 flex items-center gap-1 rounded-full border border-line bg-panel p-0.5 w-fit">
          <button
            type="button"
            onClick={() => setView("arc")}
            className={cn(
              "rounded-full px-3 py-1.5 text-sm font-semibold transition-colors",
              view === "arc" ? "bg-panel-elevated text-text" : "text-muted hover:text-text",
            )}
          >
            Arc order
          </button>
          <button
            type="button"
            onClick={() => setView("category")}
            className={cn(
              "rounded-full px-3 py-1.5 text-sm font-semibold transition-colors",
              view === "category" ? "bg-panel-elevated text-text" : "text-muted hover:text-text",
            )}
          >
            By category
          </button>
        </div>
      )}

      {view === "arc" ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div>
            {idea.cards.length > 0 && (
              <InsertGap
                active={canReorder}
                onInsert={() =>
                  setInsertAt({ kind: "before", beforeId: idea.cards[0]!.id })
                }
              />
            )}
            {insertAt?.kind === "before" && insertAt.beforeId === idea.cards[0]?.id && (
              <AddCardForm
                ideaId={idea.id}
                beforeId={insertAt.beforeId}
                onDone={() => setInsertAt(null)}
              />
            )}

            {idea.cards.map((card) => (
              <div key={card.id}>
                <DraggableComposeCard
                  card={card}
                  ideaId={idea.id}
                  canReorder={canReorder}
                />
                <InsertGap
                  active={canReorder}
                  onInsert={() => setInsertAt({ kind: "after", afterId: card.id })}
                />
                {insertAt?.kind === "after" && insertAt.afterId === card.id && (
                  <AddCardForm
                    ideaId={idea.id}
                    afterId={card.id}
                    onDone={() => setInsertAt(null)}
                  />
                )}
              </div>
            ))}
          </div>
          <DragOverlay>
            {activeCard ? (
              <div className="opacity-90 shadow-focus">
                <ComposeCard card={activeCard} ideaId={idea.id} canReorder={false} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => {
            const open = !collapsed.has(group.key);
            const colors = researchCategoryClasses(
              group.key === "yours" || group.key === "verified" || group.key === "other"
                ? null
                : group.key,
            );
            return (
              <section key={group.key}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  className="mb-2 flex w-full items-center gap-2 text-left"
                >
                  <ChevronDown
                    className={cn(
                      "size-4 text-faint transition-transform",
                      !open && "-rotate-90",
                    )}
                  />
                  <span
                    className={cn(
                      "rounded-sm border px-1.5 py-0.5 text-xs font-semibold tracking-wider uppercase",
                      colors.text,
                      colors.bg,
                      colors.border,
                    )}
                  >
                    {categoryLabel(group.key)}
                  </span>
                  <span className="text-xs text-faint">{group.cards.length}</span>
                </button>
                {open && (
                  <div className="space-y-2">
                    {group.cards.map((card) => (
                      <ComposeCard
                        key={card.id}
                        card={card}
                        ideaId={idea.id}
                        canReorder={false}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {idea.cards.length === 0 && !pasteOpen && (
        <EmptyState
          title="No cards yet"
          description="Paste research from your AI chat, or add a line of your own."
          action={
            <Button variant="secondary" size="sm" onClick={() => setPasteOpen(true)}>
              <ClipboardPaste className="size-3.5" />
              Paste research
            </Button>
          }
        />
      )}

      <div className="mt-6 space-y-3">
        {insertAt?.kind === "end" && (
          <AddCardForm ideaId={idea.id} onDone={() => setInsertAt(null)} />
        )}
        <div className="flex flex-wrap items-center gap-2">
          {!pasteOpen && (
            <Button variant="secondary" size="sm" onClick={() => setPasteOpen(true)}>
              <ClipboardPaste className="size-3.5" />
              Paste research
            </Button>
          )}
          {insertAt?.kind !== "end" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setInsertAt({ kind: "end" })}
            >
              <Plus className="size-3.5" />
              Add card
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function ComposePage() {
  const { id } = useParams();
  const detail = useIdeaDetail(id);
  const update = useUpdateIdea(id ?? "");
  const copyPrompt = useCopyResearchPrompt(id, "initial");
  const copyContinue = useCopyResearchPrompt(id, "continue");
  const hasResearchCards = Boolean(
    detail.data?.cards.some((c) => c.kind === "research"),
  );

  if (!id) {
    return (
      <EmptyState
        title="Missing idea"
        description="Open an idea from the Ideas list."
        action={
          <Button variant="secondary" asChild>
            <Link to="/ideas">Back to Ideas</Link>
          </Button>
        }
      />
    );
  }

  const idea = detail.data;

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 border-b border-line px-5 py-4">
        <div className="mx-auto w-full max-w-compose">
          <Button variant="ghost" size="sm" className="-ml-2 mb-2 h-7 gap-1 px-2 text-muted" asChild>
            <Link to="/ideas">
              <ArrowLeft className="size-4" />
              Ideas
            </Link>
          </Button>

          {detail.isLoading && (
            <div className="flex items-center gap-3">
              <Skeleton className="size-14 rounded-md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-4 w-1/3" />
              </div>
            </div>
          )}

          {detail.isError && (
            <QueryErrorState
              error={detail.error}
              onRetry={() => void detail.refetch()}
              actionLabel="load idea"
            />
          )}

          {idea && (
            <div className="flex items-start gap-3">
              <EntityAvatar src={idea.cover_url} alt="" size="lg" rounded="sm" />
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-2xl font-semibold tracking-tight">{idea.title}</h1>
                <p className="mt-1 truncate text-sm text-muted">
                  {[
                    idea.artists.map((a) => a.name).filter(Boolean).join(", ") || null,
                    idea.year,
                    idea.subtitle,
                  ]
                    .filter(Boolean)
                    .join(" · ") || idea.entity_type}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant={idea.used ? "secondary" : "ghost"}
                    size="sm"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ used: !idea.used })}
                    className={cn(idea.used && "text-text")}
                  >
                    <Check className="size-3.5" />
                    {idea.used ? "Marked used" : "Mark used"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={copyPrompt.isPending}
                    title="First-pass research prompt for a new AI chat"
                    onClick={() => copyPrompt.mutate()}
                  >
                    <Sparkles className="size-3.5" />
                    Research prompt
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={copyContinue.isPending || !hasResearchCards}
                    title={
                      hasResearchCards
                        ? "Follow-up dig — same JSON shape, only what you ask next"
                        : "Paste an initial research JSON first"
                    }
                    onClick={() => copyContinue.mutate()}
                  >
                    <Sparkles className="size-3.5" />
                    Continue research
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6">
        <div className="mx-auto w-full max-w-compose">
          {idea && <ComposeStack idea={idea} />}
        </div>
      </div>
    </div>
  );
}
